import { useCallback, useState } from "react";
import { useUser } from "@clerk/clerk-react";

/**
 * Whether this user has put their hand up for the portfolio page, and how to
 * record that they have.
 *
 * Kept in `localStorage` rather than sent anywhere: there is no early-access
 * endpoint yet, and the answer this holds is only about what the screen says
 * back to them — the button, or the line that replaces it. The day the backend
 * keeps a list, this is the one place that has to start asking it.
 *
 * Keyed on the Clerk user id, not on the browser: a shared machine must not
 * show one person's confirmation to the next one who signs in.
 *
 * Must be used inside a Clerk provider.
 */

export interface EarlyAccess {
  /** Whether they are already on the list, and so should see the confirmation. */
  joined: boolean;
  /** Records them as on it. Safe to call twice. */
  join: () => void;
}

const keyFor = (userId: string): string =>
  `naurist:portfolio-early-access:${userId}`;

// Storage can throw outright — Safari private mode, or a browser with site data
// blocked. Someone whose answer cannot be remembered is offered the button
// again next load, which is a far better failure than the screen not rendering.
const readJoined = (userId: string): boolean => {
  try {
    return window.localStorage.getItem(keyFor(userId)) === "1";
  } catch {
    return false;
  }
};

const rememberJoined = (userId: string): void => {
  try {
    window.localStorage.setItem(keyFor(userId), "1");
  } catch {
    /* not fatal — see above */
  }
};

export function useEarlyAccess(): EarlyAccess {
  const { user } = useUser();
  const userId = user?.id ?? "";

  // Joining within this page's life. The stored flag covers reloads; this is
  // what swaps the button for the confirmation the moment they press it, and is
  // also what carries the answer where storage is unavailable.
  const [joinedNow, setJoinedNow] = useState(false);

  const join = useCallback(() => {
    setJoinedNow(true);
    if (userId) rememberJoined(userId);
  }, [userId]);

  // Read during render rather than in an effect, so an already-joined user
  // never sees the button flash up for a frame before it is replaced. No id yet
  // means Clerk is still restoring, and there is nobody to look the answer up
  // for — the button stands until there is.
  const joined = joinedNow || (!!userId && readJoined(userId));

  return { joined, join };
}

export default useEarlyAccess;
